import React, { useEffect, useState } from "react";
import { Button, LogoLoader, TableAdv, showToast, Alert, CustomToggle, StatusTags } from "../../../../../components";
import { mapTableRows } from "../../../../../utils";
import { useNavigate, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { fetchResumeMiningStart } from "../../../../../redux/slices/bdmjobs/bdmjobsSlice";
import { formatWords2 } from "../../../../../utils/stringUtils";
import {  updateResumeMining } from "../../../../../redux/slices/bdmjobs/bdmjobsApi";

const ResumeMining = ({ jobId: propJobId, jobDetails }) => {
  const jobId = Number(propJobId || jobDetails?.jobId?.uiid || jobDetails?.uiid || sessionStorage.getItem("selectedJobUiid"));
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const resumeMiningData = useSelector(
    (state) => state.bdmJobs?.resumeMining?.data || []
  );
  const { user: authUser } = useSelector((state) => state.auth);
  const loading = useSelector(
    (state) => state.bdmJobs?.resumeMining?.loading
  );
  // const [showDeleteAlert, setShowDeleteAlert] = useState(false);
  // const [deleteUiid, setDeleteUiid] = useState(null);

  const colsResumeMining = [
    {
      key: "sNo",
      label: "S.No",
    },
    {
      key: "jobCode",
      label: "Job Code",
      render: (row) => <div className="text-info">{row.jobCode}</div>,
    },
    { key: "scheduledOn", label: "Scheduled On" },
    {
      key: "scheduledType",
      label: "Scheduled Type",
      render: (row) => {
        const typeLabels = {
          1: "On selected Date",
          2: "Till selected Date",
          3: "Continue until disabled",
        };
        return typeLabels[row.scheduleType] || "-";
      },
    },
    { key: "scheduledBy", label: "Scheduled By" },
    {
      key: "jobTitle",
      label: "Job Title",
    },
    {
      key: "resumeScheduled",
      label: "Resume Scheduled",
    },
    {
      key: "utcTime",
      label: "UTC Time",
    },
    {
      key: "status",
      label: "Status",
      render: (row) => {
        const statusLabels = {
          pending: "Scheduled",
          progress: "In Progress",
          done: "Completed",
          paused: "Paused",
        };
        return <StatusTags tag={statusLabels[row.status] || "Unknown"} size="xs" variant="outlined" />;
      },
    },
    {
      key: "actions",
      label: "Actions",
      render: (row) => {
        const isCompleted = row.status === "done";
        const isPaused = row.status === "paused";
        const isActive = row.status === "pending" || row.status === "progress";
        const canControl = isActive || isPaused;

        return (
          <div className="flex items-center justify-between gap-2">
            {canControl && (
              <CustomToggle
                value={isActive}
                onChange={(val) => handleUpdateStatus(row.uiid, val ? 1 : 4)}
                size="xs"
              />
            )}
            <Button
              type="icon"
              variant="text"
              icon="visibility"
              size="sm"
              color="black"
            />
            {!isCompleted && (
              <>
                <Button
                  type="icon"
                  variant="text"
                  icon="edit_square"
                  size="sm"
                  color="black"
                  onClick={() =>
                    navigate("/ats/jobs/job-details/schedule-resume-mining", {
                      state: {
                        jobId: jobId,
                        jobCode: jobDetails?.jobInfo?.jobCode || "",
                        task: row.raw, // Pass original task data for editing
                      },
                    })
                  }
                />
                {/* <Button
                  type="icon"
                  variant="text"
                  icon="delete"
                  size="sm"
                  color="danger"
                  onClick={() => {
                    setDeleteUiid(row.uiid);
                    setShowDeleteAlert(true);
                  }}
                /> */}
              </>
            )}
          </div>
        );
      },
    },
  ];


  const rowsResumeMining = resumeMiningData.map((task, i) => {
    const status = Number(task.status) || 1; // Default to Pending if missing
    let statusKey = "done";
    if (status === 1) statusKey = "pending";
    else if (status === 2) statusKey = "progress";
    else if (status === 4) statusKey = "paused";

    return {
      sNo: i + 1,
      jobCode: jobDetails?.jobInfo?.jobCode || "",
      scheduledOn: task.scheduleDate?.slice(0, 10) || "-",
      scheduledBy: formatWords2(task.addedBy?.firstName || "") + " " + formatWords2(task.addedBy?.lastName || ""),
      jobTitle: formatWords2(jobDetails?.jobId?.title || ""),
      resumeScheduled: task.maximumProfiles || 0,
      utcTime: task.interval || "-",
      status: statusKey,
      scheduleType: task.scheduleType,
      uiid: task.uiid,
      raw: task
    };
  });

  const mappedRowsResumeMining = mapTableRows(
    rowsResumeMining,
    colsResumeMining
  );

  const location = useLocation();

  const fetchSubmissions = async () => {
    if (!jobId) {
      return;
    }
    try {
      const action = {
        coll: "ResumeMining",
        fil: JSON.stringify({ "jobPostId": { "type": "NONE", "value": jobId } }),
        extret: "uiid firstName lastName"
      };
      dispatch(fetchResumeMiningStart(action));
    } catch (err) {
      console.error("Error fetching ResumeMining:", err);
    }
  };

  const handleUpdateStatus = async (uiid, status) => {
    try {
      // Find the task
      const task = resumeMiningData.find((t) => t.uiid === uiid);
      if (!task) return;

      const userId = Number(authUser?.data?.uiid) || 0;
      if (!userId) {
        showToast("Invalid user ID", "error");
        return;
      }

      const statusNumber = Number(status);

      // Get existing history and sanitize to ensure everything is "number only" where expected
      const rawHistory = Array.isArray(task.statusHistory) ? task.statusHistory : [];
      const sanitizedHistory = rawHistory
        .map((entry) => ({
          status: Number(entry.status),
          // Extract uiid if enriched, otherwise cast to number
          modifiedBy: entry.modifiedBy?.uiid ? Number(entry.modifiedBy.uiid) : Number(entry.modifiedBy || 0),
          modifiedOn: entry.modifiedOn ? new Date(entry.modifiedOn) : new Date(),
        }))
        .filter((entry) => entry.modifiedBy !== 0);

      // Create new history entry with numeric status and ID
      const newEntry = {
        status: statusNumber,
        modifiedBy: userId,
        modifiedOn: new Date(),
      };

      // Create updated history: append latest and keep only the last 10 (FIFO)
      let updatedHistory = [...sanitizedHistory, newEntry];
      if (updatedHistory.length > 10) {
        updatedHistory = updatedHistory.slice(-10); // Take the last 10 entries
      }

      // Call API to update status and history
      const res = await updateResumeMining({
        uiid,
        payload: [
          {
            type: "SET",
            value: {
              status: statusNumber,         // update current status
              statusHistory: updatedHistory // update history
            },
          },
        ],
      });

      if (res.success) {
        showToast(
          `Task ${statusNumber === 4 ? "paused" : "resumed"} successfully`,
          "success"
        );
        fetchSubmissions(); // refresh table
      } else {
        showToast(res.msg || "Failed to update task", "error");
      }
    } catch (err) {
      showToast("An error occurred", "error");
    }
  };




  // const handleDelete = async () => {
  //   try {
  //     const res = await deleteResumeMining({ uiid: deleteUiid });
  //     if (res.success) {
  //       showToast("Resume Mining task deleted successfully", "success");
  //       fetchSubmissions();
  //     } else {
  //       showToast(res.msg || "Failed to delete task", "error");
  //     }
  //   } catch (err) {
  //     console.error("Error deleting resume mining:", err);
  //     showToast("An error occurred while deleting", "error");
  //   } finally {
  //     setShowDeleteAlert(false);
  //     setDeleteUiid(null);
  //   }
  // };

  useEffect(() => {
    if (jobId) {
      fetchSubmissions();
    }
  }, [jobId, location.key]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LogoLoader />
      </div>
    );
  }

  return (
    <div>
      <div className=" flex justify-between p-2">
        <h5 className="my-2">Resume mining</h5>
        <div className="flex gap-2">
          {/* {resumeMiningData.length > 0 && Number(resumeMiningData[0].status) !== 3 && (
            <Button
              title={"Edit"}
              variant="outlined"
              icon="edit_square"
              size="sm"
              className="font-[700]"
              onClick={() => {
                const latestTask = resumeMiningData[0];
                navigate("/ats/jobs/job-details/schedule-resume-mining", {
                  state: {
                    jobId: jobId,
                    jobCode: jobDetails?.jobInfo?.jobCode || "",
                    task: latestTask,
                  },
                });
              }}
            />
          )} */}
          <Button
            title={"Schedule"}
            variant="outlined"
            size="sm"
            className="font-[700]"
            onClick={() =>
              navigate("/ats/jobs/job-details/schedule-resume-mining", {
                state: {
                  jobId: jobId,
                  jobCode: jobDetails?.jobInfo?.jobCode || "",
                },
              })
            }
          />
        </div>
      </div>
      <div>
        <TableAdv
          tableId={"jobs_jobdetails_resumemining_table"}
          columns={colsResumeMining}
          rows={mappedRowsResumeMining}
          hideToolBar
        />
      </div>
      {/* <Alert
        isOpen={showDeleteAlert}
        onClose={() => {
          setShowDeleteAlert(false);
          setDeleteUiid(null);
        }}
        title="Delete Scheduled Job"
        description="Are you sure you want to delete this scheduled resume mining task? This action cannot be undone."
        acceptButton={{
          show: true,
          title: "Delete",
          onClick: handleDelete,
        }}
      /> */}
    </div>
  );
};

export default ResumeMining;
